import output from "_src/engine/output";

import {propertyType, objectType, cadType, propertyInitStatus} from "_src/constants";
import { colorUtils } from "_src/utils";

let reported = {};

const HSL_THRESHOLD = { h: 2 / 360, s: 1 / 100, l: 1 };

export function createProperty(type) {
  switch(type) {
  case propertyType.LEVEL:
    return createLevelProperty();
  case propertyType.ASSET:
    return createAssetProperty();
  case propertyType.OBJECT:
    return createObjectProperty();
  case propertyType.FRAME:
    return createFrameProperty();
  case propertyType.GROUP:
    return createGroupProperty();
  case propertyType.MEASURE:
    return createMeasureProperty();
  case propertyType.PHOTO_GROUP:
    return createPhotoGroupProperty();
  }
}

export function reformValue(data, fieldName, generateValueMethod, reportName) {

  if (!valueExists(data, fieldName) && reportName) {
    reportOnce(reportName, fieldName);
  }

  if (!valueExists(data, fieldName) || valueIsIllegal(data, fieldName)) {
    return generateValueMethod();
  } else {
    return data[fieldName];
  }

}

export function generateGroupColor(shades) {

  if (shades === undefined || shades.length === 0 ) {
    return colorUtils.generateHexColor();
  }

  const hslShades = shades.map(color => colorUtils.rgbToHsl(color));

  if (!checkShadesCompatibility(hslShades)) {
    return colorUtils.generateHexColor();
  }

  hslShades.sort((a, b) => a.l - b.l);

  const shadesCount = shades.length;

  const theDarkestShade = hslShades[0];
  const theLightestShade = hslShades[shadesCount - 1];
  const step = (shadesCount - 1) > 0 ? (theLightestShade.l - theDarkestShade.l) / (shadesCount - 1) : theDarkestShade.l / 2;
  const groupShade = { ...theDarkestShade, l: Math.max(theDarkestShade.l - step, 0) };

  const rgbColor = colorUtils.hslToRgb(groupShade);

  return colorUtils.rgbToHex(rgbColor[0], rgbColor[1], rgbColor[2]);

}

export function getHexPaletteUnique(colorList) {
  const color = colorUtils.getPaletteColorUnique(colorList);
  colorList.push(color);
  return color;
}

export function deepCopy(object) {
  return JSON.parse(JSON.stringify(object));
}

export function cleanup() {
  reported = {};
}

export function getCadType(objectData) {
  const cadTypeString = objectData["type"];
  switch (cadTypeString) {
    case "model":
      return cadType.MODEL;
    case "cylinder":
      return cadType.CYLINDER;
    case "cone":
      return objectData["properties"]["truncated"] ? cadType.TRUNCATED_CONE : cadType.CONE;
    case "sphere":
      return cadType.SPHERE;
    case "plane":
      return cadType.PLANE;
    case "torus":
      return cadType.TORUS;
    case "free-form":
      return cadType.FREEFORM;
    default:
      return cadType.UNDEFINED;
  }
}

class Property {

  constructor() {
    this._property = {};
  }

  getValue(name) {
    if (!(name in this._property)) {
      throw `Couldn't find property ${name}`;
    }
    return this._property[name];
  }

  setValue(name, value) {
    this._property[name] = value;
  }

  insertValue(name, value) {
    if (!(name in this._property)) {
      throw `Couldn't find property ${name}`;
    }
    this._property[name] = value;
  }

  getPropertyMap() {
    return deepCopy(this._property);
  }

}

function createAssetProperty() {
  const property = new Property();

  property.setValue("id", "");
  property.setValue("box", []);
  property.setValue("grid", []);
  property.setValue("name", "");
  property.setValue("levels", []);
  property.setValue("attrs", null);

  return property;
}

function createLevelProperty() {
  const property = new Property();

  property.setValue("id", "");
  property.setValue("file", "");
  property.setValue("hash", "");
  property.setValue("width", 0);
  property.setValue("height", 0);
  property.setValue("channels", 0);
  property.setValue("index", 0);
  property.setValue("name", "");
  property.setValue("points", 0);
  property.setValue("triangles", 0);

  return property;
}

function createObjectProperty() {
  const property = new Property();
  property.setValue("initStatus", propertyInitStatus.INCOMPLETE);

  property.setValue("name", "");
  property.setValue("id", "");
  property.setValue("groupID", "");
  property.setValue("visible", true);
  property.setValue("locked", false);
  property.setValue("color", "");
  property.setValue("uuid", "");
  property.setValue("scannertype", "");
  property.setValue("deviceserial", "");
  property.setValue("datasize", 0);
  property.setValue("surfelsSize", 0);
  property.setValue("texturedSize", 0);
  property.setValue("untexturedSize", 0);
  property.setValue("isHD", false);
  property.setValue("hasrawgeometry", false);
  property.setValue("targetRawScan", "");

  property.setValue("texturegain", 0.0);
  property.setValue("texturecameratype", 0);
  property.setValue("textureillumination", 0.0);
  property.setValue("globalapplied", false);
  property.setValue("type", -1);

  property.setValue("textures", []);

  property.setValue("frames", []);

  property.setValue("transform", [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  property.setValue("projection", [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0]);
  property.setValue("correction", [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0]);

  property.setValue("surfelsfile", {});
  property.setValue("geometriesfile", {});
  property.setValue("framesfile", {});

  property.setValue("numPolygons", 0);
  property.setValue("numVertices", 0);
  property.setValue("numSurfels", 0);
  property.setValue("maxError", 0);
  property.setValue("numFrames", 0);
  property.setValue("numTexturedFrames", 0);

  property.setValue("textureWidth", 0);
  property.setValue("textureHeight", 0);

  property.setValue("cadData", {});
  property.setValue("cadModel", {});
  property.setValue("cadType", "");
  property.setValue("cadProperties", {});

  //cads experimental
  property.setValue("fitmaskfile", {});
  property.setValue("fitMaskFrames", []);
  property.setValue("assembly", {});
  property.setValue("referencedFusion", "");

  return property;
}

function createFrameProperty() {
  const property = new Property();

  property.setValue("name", "");
  property.setValue("id", "");
  property.setValue("color", "");
  property.setValue("error", 0.0);
  property.setValue("keyframe", false);

  property.setValue("numPolygons", 0);
  property.setValue("numVertices", 0);
  property.setValue("numSurfels", 0);
  property.setValue("textured", false);

  property.setValue("transform", [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  property.setValue("textures", []);
  property.setValue("geometries", []);
  property.setValue("trackings", []);

  return property;
}

function createGroupProperty() {
  const property = new Property();

  property.setValue("name", "");
  property.setValue("id", "");
  property.setValue("uuid", "");
  property.setValue("color", "");
  property.setValue("visible", true);
  property.setValue("locked", true);
  property.setValue("groupID", "");
  property.setValue("type", objectType.GROUP);

  property.setValue("numPolygons", 0);
  property.setValue("numVertices", 0);
  property.setValue("textured", false);

  property.setValue("transform", [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  property.setValue("textures", []);
  property.setValue("geometries", []);

  return property;
}

function createMeasureProperty() {
  const property = new Property();
  property.setValue("initStatus", propertyInitStatus.INCOMPLETE);

  property.setValue("name", "");
  property.setValue("id", "");
  property.setValue("uuid", "");
  property.setValue("visible", false);
  property.setValue("selected", false);
  property.setValue("properties", {});

  //for lines
  property.setValue("type", -1);

  //for distance maps
  property.setValue("searchDistance", -1);

  //for internal use
  property.setValue("geometriesSource", "");
  property.setValue("geometries", null);
  property.setValue("measuredObjects", []);

  return property;
}

function createPhotoGroupProperty() {
  const property = new Property();

  property.setValue("name", "");
  property.setValue("id", "");
  property.setValue("initStatus", propertyInitStatus.INCOMPLETE);
  property.setValue("uuid", "");
  property.setValue("groupID", "");
  property.setValue("visible", false);
  property.setValue("type", objectType.PHOTO_GROUP);
  property.setValue("projection", [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0]);
  property.setValue("transform", [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
  property.setValue("properties", {});
  property.setValue("scenePhotoSize", 0);
  property.setValue("registered", false);

  property.setValue("photos", null);
  property.setValue("undistortionMap", null);

  //for internal use
  property.setValue("photosSource", "");

  return property;
}

function valueExists(data, fieldName) {
  return fieldName in data;
}

function valueIsIllegal(data, fieldName) {
  return data[fieldName] === "";
}

function reportOnce(source, missingAttribute) {

  if (!reported[source]) {
    reported[source] = new Set();
  }

  if (reported[source].has(missingAttribute)) {
    return;
  }

  reported[source].add(missingAttribute)

  output.warn("\"" + source + "\" doesn't have the \"" + missingAttribute + "\" field!");

}

function checkShadesCompatibility(shades) {

  let hmin = shades[0].h,
      hmax = shades[0].h,
      smin = shades[0].s,
      smax = shades[0].s;

  for (let shade of shades) {
    if (shade.h > hmax) {
      hmax = shade.h;
    } else if (shade.h < hmin) {
      hmin = shade.h;
    }
    if (shade.s > smax) {
      smax = shade.s;
    } else if (shade.s < hmin) {
      smin = shade.s;
    }
  }

  return (hmax - hmin < HSL_THRESHOLD.h) && (smax - smin < HSL_THRESHOLD.s);

}



